import { useQuery } from "@tanstack/react-query";
import type { CarouselResultsModel, CategoryGridItem } from "src/http/types";
import { useAppContext } from "src/shared/context/AppContext";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import type { Location } from "src/types/shared/cookies";
import { ALTERNATIVE_PERFORMERS_CAROUSEL_ITEMS_KEY } from "src/website-apps/shared/constants/carousel-keys";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";

/**
 * Reactively reads alt performer items from the carousel query cache.
 * PerformerCarouselsV2 owns the fetch; this hook subscribes to the same
 * cache key and re-renders when data arrives. No duplicate network request.
 *
 * @param overrideLocation - when provided (e.g. residency venue coords),
 *   used instead of the cookie location so the query key matches what
 *   PerformerCarouselsV2 fetched with.
 */
export const useAltPerformerItems = (
  overrideLocation?: Pick<Location, "lt" | "lg">,
): readonly CategoryGridItem[] | undefined => {
  const appContext = useAppContext();
  const [state] = useEventsGridContext();
  const { location } = state.filters;

  const effectiveLt = overrideLocation?.lt ?? location?.lt;
  const effectiveLg = overrideLocation?.lg ?? location?.lg;

  const { data: carouselData } = useQuery<CarouselResultsModel>({
    queryKey: createQueryKey(QueryKeyPrefix.PERFORMER_CAROUSELS, {
      categoryId: appContext.categoryId,
      lt: effectiveLt ?? 0,
      lg: effectiveLg ?? 0,
    }),
    queryFn: () => Promise.reject(new Error("useAltPerformerItems is a cache-only observer")),
    enabled: false,
  });

  return carouselData?.[ALTERNATIVE_PERFORMERS_CAROUSEL_ITEMS_KEY] ?? undefined;
};
